// Payouts (SM-714 / SM-710). The exchange's finance team prepares,
// approves and pays every batch by hand, outside Smart Market (SM-878:
// no schedule, no forecast, no payout destination); broker staff read
// them. Each batch shows its state and the NEXT ACTION in plain words
// (lib/payouts.ts — one sentence per state, pinned by test), and held
// earnings are called out because a hold keeps them out of a batch. One
// line points to Settlement for the node's net position and payment requests.
//
//   GET /api/broker/{id}/payouts?currency=&state=&offset=&limit=   PayoutBatchListResponse
//   GET /api/broker/{id}/revenue/balances                          (held figure)
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { capabilityGuard, Guarded } from "@/components/layout/guard";
import { PageHeader } from "@/components/layout/PageHeader";
import { useScope, useShellSearch } from "@/components/layout/ScopeContext";
import { SectionCard } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Money } from "@/components/ui/Money";
import { Id, Pager, TableFrame, Time } from "@/components/ui/Common";
import { EmptyState, QueryBlock } from "@/components/ui/States";
import { Select } from "@/components/ui/Field";
import { fetchPayouts, fetchRevenueBalances, keys } from "@/lib/queries";
import { payoutNextAction, payoutStateLabel, payoutStateTone, payoutStates } from "@/lib/payouts";
import type { PayoutBatchItem } from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { labelOf } from "@/i18n/labels";

export const Route = createFileRoute("/_app/payouts")({
  beforeLoad: capabilityGuard("FINANCE_VIEW"),
  component: PayoutsPage,
});

function PayoutsPage() {
  const { t } = useI18n();
  const { guard } = Route.useRouteContext();
  const { nodeId, currency } = useScope();
  const shell = useShellSearch();
  const params = batchParams(currency, "", 0);
  const probe = { queryKey: keys.payouts(nodeId, params), queryFn: () => fetchPayouts(nodeId, params) };
  const balancesQ = useQuery({ queryKey: keys.revenueBalances(nodeId), queryFn: () => fetchRevenueBalances(nodeId), enabled: guard.kind === "allow" });
  const held = (balancesQ.data?.balances ?? []).find((b) => b.currency === currency)?.held;

  return (
    <Guarded guard={guard} probe={probe}>
      <PageHeader title={t("payouts.title")} subtitle={t("payouts.subtitle")} coverage="node" money />
      <div className="space-y-4">
        <p className="text-xs text-t-2">
          {t("payouts.manualNote")}{" "}
          <Link to="/settlement" search={shell} className="font-semibold text-neon hover:underline">
            {t("payouts.settlementLink")}
          </Link>
        </p>
        {held && held !== "0" && (
          <p className="banner warn mb-0 text-xs" role="status">
            {t("payouts.heldNote")} <Money currency={currency} value={held} />
          </p>
        )}
        {/* keyed by node and currency: a switch starts at the first page with no filter (SM-943) */}
        <Batches key={`${nodeId}|${currency}`} nodeId={nodeId} currency={currency} enabled={guard.kind === "allow"} />
      </div>
    </Guarded>
  );
}

function batchParams(currency: string, state: string, offset: number) {
  return { currency, state: state || undefined, offset, limit: 20 };
}

function Batches({ nodeId, currency, enabled }: { nodeId: string; currency: string; enabled: boolean }) {
  const { t } = useI18n();
  const [state, setState] = useState("");
  const [offset, setOffset] = useState(0);
  const params = batchParams(currency, state, offset);
  const batchesQ = useQuery({ queryKey: keys.payouts(nodeId, params), queryFn: () => fetchPayouts(nodeId, params), enabled });
  return (
    <SectionCard
      title={t("payouts.batches")}
      actions={
        <div className="w-48">
          <Select
            label={t("common.status")}
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setOffset(0);
            }}
            options={[
              { value: "", label: t("common.all") },
              ...payoutStates().map((s) => ({ value: s, label: t(payoutStateLabel(s)!) })),
            ]}
          />
        </div>
      }
    >
      <QueryBlock query={batchesQ} empty={(d) => ((d.items ?? []).length === 0 && !d.page?.total ? <EmptyState title={t("payouts.none")} /> : null)}>
        {(d) => (
          <>
            {/* a page past the end (rows gone since it was opened) keeps the pager to step back */}
            {(d.items ?? []).length === 0 ? (
              <EmptyState title={t("payouts.none")} />
            ) : (
              <TableFrame label={t("payouts.batches")}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">{t("payouts.cutoff")}</th>
                      <th scope="col">{t("common.status")}</th>
                      <th scope="col">{t("payouts.nextAction")}</th>
                      <th scope="col" className="text-right">
                        {t("payouts.total")}
                      </th>
                      <th scope="col">{t("payouts.items")}</th>
                      <th scope="col">{t("payouts.batch")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.items ?? []).map((b) => (
                      <BatchRow key={b.batch_id} b={b} />
                    ))}
                  </tbody>
                </table>
              </TableFrame>
            )}
            <Pager page={d.page} onOffset={setOffset} />
          </>
        )}
      </QueryBlock>
    </SectionCard>
  );
}

function BatchRow({ b }: { b: PayoutBatchItem }) {
  const { t } = useI18n();
  const label = payoutStateLabel(b.state);
  const next = payoutNextAction(b.state);
  return (
    <tr>
      <td className="text-xs">
        <Time ms={b.cutoff_unix_ms} />
        <span className="block text-2xs text-t-3">{labelOf(t, "schedule", b.schedule)}</span>
      </td>
      <td>
        <Badge tone={payoutStateTone(b.state)}>{label ? t(label) : b.state}</Badge>
      </td>
      <td className="max-w-72 text-xs text-t-2">
        {next ? t(next) : t("payouts.next.unknown")}
        {b.state === "FAILED" && b.failure_reason && (
          <span className="block text-2xs text-no">{t("payouts.failureReason", { reason: b.failure_reason })}</span>
        )}
      </td>
      <td className="text-right">
        <Money currency={b.currency} value={b.total} />
      </td>
      <td className="num text-xs">{String(b.item_count)}</td>
      <td>
        <Id value={b.batch_id} />
      </td>
    </tr>
  );
}
